import React from 'react';
import PropTypes from 'prop-types';
import { createVariants } from './_variants';

const tabGroupClasses = createVariants({
  base: 'flex gap-1 p-1 rounded-lg',
  variants: {
    default: 'bg-[var(--succession-bg-secondary)]',
    pills: 'bg-transparent gap-2',
    solid: 'bg-transparent gap-2',
    underline: 'bg-transparent border-b border-[var(--succession-border)] gap-0 p-0',
  },
  sizes: {
    sm: 'text-xs',
    md: 'text-[13px]',
    lg: 'text-sm',
  },
  defaultVariant: 'default',
  defaultSize: 'md',
});

/**
 * TabGroup - Consistent tab navigation with optional count badges
 * 
 * Features:
 * - Multiple variants (segmented, pills, underline)
 * - Count badges
 * - Keyboard navigation support
 * - Full accessibility (ARIA roles)
 * - Tokenized styling
 */
const TabGroup = ({
  tabs = [],
  activeTab,
  onChange,
  variant = 'default',
  size = 'md',
  showCounts = true,
  className = '',
}) => {
  const isActive = (key) => key === activeTab;

  const getTabClasses = (key) => {
    const base = 'cursor-pointer transition-all flex items-center gap-1.5 font-medium';
    const interactive = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--succession-focus-ring)]';
    
    if (variant === 'underline') {
      return `${base} ${interactive} py-2 px-3 border-b-2 ${
        isActive(key)
          ? 'border-[var(--succession-primary)] text-[var(--succession-text-primary)] font-semibold'
          : 'border-transparent text-[var(--succession-text-secondary)] hover:text-[var(--succession-text-primary)] hover:border-[var(--succession-border)]'
      }`;
    }

    // solid — filled accent pill for the active tab. Mirrors the Rolodex tab bar
    // (SuccessionRolodex.jsx) so section switchers read the same across Succession.
    if (variant === 'solid') {
      // Config-mapped utilities, not bg-[var(--…)] arbitrary values — these are the exact
      // classes the Rolodex tab bar uses, so they are guaranteed present in the built CSS.
      return `${base} ${interactive} px-4 py-2 text-sm rounded-full duration-200 ${
        isActive(key)
          ? 'bg-succession-primary text-white shadow-sm'
          : 'text-succession-text-secondary hover:text-succession-text-primary'
      }`;
    }

    if (variant === 'pills') {
      return `${base} ${interactive} py-1.5 px-3 rounded-full ${
        isActive(key)
          ? 'bg-[var(--succession-bg-primary)] text-[var(--succession-text-primary)] font-semibold shadow-sm'
          : 'bg-transparent text-[var(--succession-text-secondary)] hover:bg-[var(--succession-hover-bg)] hover:text-[var(--succession-text-primary)]'
      }`;
    }

    // default (segmented)
    return `${base} ${interactive} py-1.5 px-3 rounded-md border-none ${
      isActive(key)
        ? 'bg-[var(--succession-bg-primary)] text-[var(--succession-text-primary)] font-semibold shadow-sm'
        : 'bg-transparent text-[var(--succession-text-secondary)] hover:bg-[var(--succession-hover-bg)] hover:text-[var(--succession-text-primary)]'
    }`;
  };

  const getCountClasses = (key) => {
    // solid renders the count inline as "(n)" rather than as a badge.
    if (variant === 'solid') {
      return `ml-1.5 text-xs ${
        isActive(key) ? 'text-white/80' : 'text-succession-text-muted'
      }`;
    }
    const base = 'py-0.5 px-1.5 rounded-full text-[11px] font-semibold min-w-[18px] text-center';
    return `${base} ${
      isActive(key)
        ? 'bg-[var(--succession-bg-secondary)] text-[var(--succession-text-secondary)]'
        : 'bg-[var(--succession-bg-tertiary)] text-[var(--succession-text-secondary)]'
    }`;
  };

  return (
    <div 
      role="tablist"
      className={tabGroupClasses({ variant, size, className })}
    >
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={isActive(tab.key)}
          onClick={() => onChange(tab.key)}
          className={getTabClasses(tab.key)}
          type="button"
        >
          {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
          <span>{tab.label}</span>
          {showCounts && tab.count !== undefined && (variant === 'solid' || tab.count > 0) && (
            <span className={getCountClasses(tab.key)}>
              {variant === 'solid' ? `(${tab.count})` : tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

TabGroup.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
      icon: PropTypes.node,
    })
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['default', 'pills', 'underline', 'solid']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showCounts: PropTypes.bool,
  className: PropTypes.string,
};

export default TabGroup;

